import * as React from 'react';
import { useCallback } from 'react';
import { observer } from 'mobx-react';

import { UitkSpacing, UitkSpacingProps } from 'uitk-react-spacing';

import { isFlightsSearch, openInSameTab, trackAnalytics } from 'utility';
import { useStores } from 'utility/hooks';
import {
  FlightActionFragment,
  FlightsShoppingPwaFlightInformationResults,
  FlightsShoppingPwaFlightSearchResultsFlightsSearch
} from '__generated__/api';
import FailedRequestMessaging = FlightsShoppingPwaFlightSearchResultsFlightsSearch.FailedRequestMessaging;
import FlightsActionableErrorMessaging = FlightsShoppingPwaFlightSearchResultsFlightsSearch.FlightsActionableErrorMessagingInlineFragment;
import ServerErrorMessaging = FlightsShoppingPwaFlightInformationResults.ServerErrorMessaging;
import { ErrorEmptyState } from '.';
import TriggerFlightsSearchParams from 'types/shared/trigger-flights-search-params';
import { useHistory, useLocation } from 'react-router-dom';
import { UitkEmptyStateActionArea } from 'uitk-react-empty-state';
import { UitkTertiaryButton } from 'uitk-react-button';

interface FailedRequestMessagingProps {
  flightsActionableErrorMessaging: FailedRequestMessaging | ServerErrorMessaging | FlightsActionableErrorMessaging;
}

const mainSpacingProps: UitkSpacingProps = {
  margin: { block: 'twelve' },
  padding: 'twelve'
};

const altDatesErrorButtonsProps: UitkSpacingProps = { margin: { blockend: 'three' } };

export const FailedRequestMessaging = observer(function FailedRequestMessaging({
  flightsActionableErrorMessaging
}: FailedRequestMessagingProps) {
  const {
    icon,
    title,
    message,
    action,
    errorRecoveryButtons
  } = flightsActionableErrorMessaging as FlightsActionableErrorMessaging;
  const { id } = icon;
  const { searchCriteriaStore, analytics, context, page } = useStores();
  const location = useLocation();
  const history = useHistory();
  const { displayAction, accessibilityMessage } = action;

  const onClickAction = useCallback(
    (error: FlightActionFragment) => () => {
      const analyticsType = error.analytics;
      trackAnalytics(analyticsType, analytics);
      if (isFlightsSearch(page)) {
        if (error.relativePackageableURI) {
          openInSameTab(error.relativePackageableURI.value);
        } else if (searchCriteriaStore === null || searchCriteriaStore.reloadPageOnError) {
          window.location.reload();
        } else {
          const triggerSearchParams: TriggerFlightsSearchParams = {
            history,
            location,
            context,
            shouldForce: true,
            replaceHistory: true
          };
          searchCriteriaStore.triggerSearch(triggerSearchParams);
        }
      } else {
        if (error.relativeURI) {
          window.location.href = error.relativeURI.relativePath;
        } else {
          window.location.reload();
        }
      }
    },
    [analytics, history, location, context, page, searchCriteriaStore]
  );

  const altDatesErrorButtons = errorRecoveryButtons ? (
    <UitkEmptyStateActionArea>
      {errorRecoveryButtons.map(altButton => (
        <UitkSpacing key={altButton.analytics.referrerId} margin={altDatesErrorButtonsProps.margin}>
          <UitkTertiaryButton key={altButton.analytics.referrerId} onClick={onClickAction(altButton)}>
            {altButton.displayAction}
          </UitkTertiaryButton>
        </UitkSpacing>
      ))}
    </UitkEmptyStateActionArea>
  ) : null;

  return (
    <UitkSpacing padding={mainSpacingProps.padding} margin={mainSpacingProps.margin}>
      <div data-test-id="failed-request-messaging">
        <ErrorEmptyState
          icon={id}
          title={title}
          body={message}
          buttonText={displayAction}
          clickHandler={onClickAction(action)}
          headingTag="h2"
          altDatesErrorButtons={altDatesErrorButtons}
        />
        {accessibilityMessage && <span className="is-visually-hidden">{accessibilityMessage}</span>}
      </div>
    </UitkSpacing>
  );
});
